import React from 'react';
import { AccordionCard } from '../../Library/AccordionCard';
import { Grid, SvgIcon, Typography } from '@mui/material';
import { ReactComponent as StressCardIcon } from '../../../images/Track-Final.svg';
import { ReactComponent as OptimalIcon } from '../../../images/optimal-icon.svg';
import { ReactComponent as AlarmedIcon } from '../../../images/zap.svg';
import { ReactComponent as ImbalancedIcon } from '../../../images/warning.svg';
import { ReactComponent as ExhaustedIcon } from '../../../images/exhausted-icon.svg';
import { StressArchetypeSlider } from './StressArchetypeSlider';
import { HormoneSnapshot, ResultType } from '../../../types';
import { colors } from '../../../utils/theme';
import { StressData } from '../../../views/StressInsights';

const resultIconMap = {
  normal: OptimalIcon,
  type_1: AlarmedIcon,
  type_2: ImbalancedIcon,
  type_3: ExhaustedIcon,
};

export const StressArchetype = ({
  snapShot,
  stressDataMap,
}: {
  snapShot: HormoneSnapshot;
  stressDataMap: Record<ResultType, StressData>;
}): JSX.Element => {
  const determineIconStyles = (
    icon: React.ComponentType
  ): { width: number; height: number; ml?: number } => {
    switch (icon) {
      case ImbalancedIcon:
        return { width: 28, height: 28 };
      case ExhaustedIcon:
        return { width: 14, height: 14, ml: 0.75 };
      default:
        return { width: 16, height: 16, ml: 0.5 };
    }
  };

  const IconComponent = resultIconMap[snapShot?.result as ResultType];
  const iconStyles = determineIconStyles(IconComponent);

  return (
    <AccordionCard
      panelId="stress-archetype"
      summary={
        <Grid container flexDirection="column" alignItems="flex-start">
          <Grid container item flexDirection="row" alignItems="center">
            <SvgIcon
              component={StressCardIcon}
              inheritViewBox
              sx={{
                width: 20,
                height: 20,
                mr: 0.5,
              }}
            />
            <Typography variant="medium" fontSize={14}>
              YOUR CORTISOL TYPE
            </Typography>
          </Grid>

          <Grid
            container
            item
            flexDirection="row"
            alignItems="center"
            sx={{ mt: 2.5 }}
          >
            <Typography fontSize={20}>
              {stressDataMap[snapShot?.result as ResultType]?.label}
            </Typography>
            <SvgIcon
              component={IconComponent}
              inheritViewBox
              sx={{
                width: iconStyles.width,
                height: iconStyles.height,
                ml: iconStyles.ml,
                fill: colors.greys.BASE_WHITE,
              }}
            />
          </Grid>

          <div
            style={{
              background: colors.greys.BG_GREY,
              padding: '4px 8px',
              borderRadius: 10,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              margin: '16px 0px 10px 0px',
            }}
          >
            <div
              style={{
                width: 12,
                height: 12,
                borderRadius: 50,
                background:
                  stressDataMap[snapShot?.result as ResultType]?.color,
                marginRight: 4,
              }}
            />
            <Typography variant="light" fontSize={12} sx={{ opacity: 0.8 }}>
              <Typography variant="mono" sx={{ marginRight: 0.5, opacity: 1 }}>
                {
                  stressDataMap[snapShot?.result as ResultType]
                    ?.stress_archetype_percentage
                }
              </Typography>
              OF PEOPLE ARE{' '}
              <Typography
                variant="medium"
                sx={{ opacity: 1, textTransform: 'uppercase' }}
              >
                {stressDataMap[snapShot?.result as ResultType]?.label}
              </Typography>
            </Typography>
          </div>

          <StressArchetypeSlider snapShot={snapShot} />
        </Grid>
      }
      details={
        <Grid
          container
          flexDirection="column"
          sx={{ color: colors.greys.TEXT_BLACK }}
        >
          <Typography fontSize={14} variant="medium" sx={{ mb: 1 }}>
            WHAT DOES THIS MEAN?
          </Typography>
          <Typography fontSize={14} variant="light" sx={{ opacity: 0.8 }}>
            {stressDataMap[snapShot?.result as ResultType]?.what_does_this_mean}
          </Typography>
          <Typography fontSize={14} variant="medium" sx={{ mb: 1, mt: 2 }}>
            WHY DOES THIS MATTER?
          </Typography>
          <Typography fontSize={14} variant="light" sx={{ opacity: 0.8 }}>
            {
              stressDataMap[snapShot?.result as ResultType]
                ?.why_does_this_matter
            }
          </Typography>
        </Grid>
      }
    />
  );
};
